import {
  FileText,
  Download,
  Info,
  ArrowRight,
  Mail,
  CheckCircle2,
  Shield,
  Stethoscope,
  Camera,
} from "lucide-react";

const FORMS_URL =
  "https://customer-assets.emergentagent.com/job_ubl-hoops/artifacts/sg64orsi_PAASS%20Master%20Participation%20Waiver.docx.pdf";

const sections = [
  {
    icon: Shield,
    title: "Liability Waiver",
    text: "Standard assumption-of-risk and release sections required for participation.",
    tag: "Required",
  },
  {
    icon: Stethoscope,
    title: "Medical & Emergency Information",
    text: "Medical authorization, treatment consent, and emergency contact details.",
    tag: "Required",
  },
  {
    icon: Camera,
    title: "Media & Photo Release",
    text: "Optional — lets us know whether your player can appear in UHL photos and posts.",
    tag: "Optional",
  },
];

export default function Forms() {
  return (
    <div data-testid="forms-page">
      {/* HERO */}
      <section className="basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-extrabold uppercase tracking-wider">
            <FileText className="w-4 h-4" />
            Parent Forms
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Everything you need, all in{" "}
            <span className="text-orange-500">one PDF.</span>
          </h1>
          <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-2xl">
            Our combined participant forms cover liability, medical
            information, and media release in a single document. Download
            once, fill it out, and send it back — that's it.
          </p>
        </div>
      </section>

      {/* INSTRUCTIONS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="forms-instructions"
            className="bg-blue-50 border border-blue-100 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row gap-5 items-start"
          >
            <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
              <Info className="w-6 h-6" />
            </div>
            <div className="flex-1 space-y-3">
              <p className="font-black text-slate-900 text-lg">
                How to submit your forms
              </p>
              <ol className="space-y-1 list-decimal list-inside text-slate-700 font-semibold">
                <li>Download the combined forms PDF below.</li>
                <li>Fill out digitally or print, sign, and scan.</li>
                <li>
                  Email the completed forms to{" "}
                  <a
                    href="mailto:unifiedhoopsleague@gmail.com"
                    className="text-blue-700 underline"
                    data-testid="forms-email-link"
                  >
                    unifiedhoopsleague@gmail.com
                  </a>{" "}
                  — or bring them to your first session.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* SINGLE DOWNLOAD CARD */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="forms-download-card"
            className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-blue-900/5 p-8 sm:p-10 md:p-12"
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
                  Required for all players
                </p>
                <h2 className="mt-2 text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
                  UHL Participant Forms
                </h2>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  One combined PDF that includes everything we need before
                  your player's first session.
                </p>
              </div>
            </div>

            {/* WHAT'S INSIDE */}
            <div className="mt-8">
              <p className="text-sm font-black uppercase tracking-[0.15em] text-slate-500">
                What's included
              </p>
              <ul className="mt-4 space-y-3">
                {sections.map(({ icon: Icon, title, text, tag }) => (
                  <li
                    key={title}
                    data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-start gap-4 rounded-2xl bg-[#FAFAFA] border border-slate-100 p-4 sm:p-5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-black text-slate-900">{title}</p>
                        <span
                          className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            tag === "Required"
                              ? "bg-blue-50 text-blue-700"
                              : "bg-orange-50 text-orange-700"
                          }`}
                        >
                          {tag}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600 font-medium leading-relaxed">
                        {text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* DOWNLOAD BUTTON */}
            <a
              href={FORMS_URL}
              download="UHL-Participant-Forms.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="forms-download-btn"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-orange-500 text-white hover:bg-orange-600 hover:-translate-y-0.5 hover:shadow-2xl transition-all font-black px-8 py-4 rounded-full text-lg"
            >
              <Download className="w-5 h-5" />
              Download Forms PDF
            </a>

            <p className="mt-4 flex items-start gap-2 text-sm text-slate-500 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              Works on desktop and mobile. The file will open or download
              based on your device's settings.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-white">
                Still have questions about the forms?
              </h3>
              <p className="mt-2 text-slate-300 font-medium">
                We're happy to walk you through anything — no question too
                small.
              </p>
            </div>
            <a
              href="mailto:unifiedhoopsleague@gmail.com"
              data-testid="forms-contact-email"
              className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 transition-all font-bold px-7 py-3 rounded-full"
            >
              <Mail className="w-4 h-4" /> Email Us{" "}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
